import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Upload, CheckCircle2, AlertTriangle, HardHat } from "lucide-react";

/**
 * Public, token-gated vendor compliance upload. Reached via
 *   {PUBLIC_APP_URL}/vendor-upload/:token
 *
 * Vendors get here from a compliance-nudge email. No login. The token is
 * server-side single-use with a 30-day TTL. Once submitted successfully the
 * token flips to used so a leaked email can't be replayed.
 */
const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function VendorUploadPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [section, setSection] = useState("coi");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/vendor-upload/${token}`);
      setCtx(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "This upload link is no longer valid.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (section === "coi") {
        payload.carrier = form.carrier || "";
        payload.policy_no = form.policy_no || "";
        payload.coverage_usd = parseFloat(form.coverage_usd) || 0;
        payload.expires_at = form.expires_at ? new Date(form.expires_at).toISOString() : "";
      } else if (section === "w9") {
        payload.ein_last4 = (form.ein_last4 || "").slice(0, 4);
        payload.signed_at = form.signed_at ? new Date(form.signed_at).toISOString() : new Date().toISOString();
      } else if (section === "license") {
        payload.number = form.number || "";
        payload.state = form.state || "";
        payload.expires_at = form.expires_at ? new Date(form.expires_at).toISOString() : "";
      }
      // Optional file capture — we accept a small data-URL. Big files would
      // need Object Storage; the property manager can nudge again if the
      // vendor tries to upload something huge here.
      let doc_data_url = null, doc_filename = null;
      const file = form._file;
      if (file) {
        if (file.size > 3 * 1024 * 1024) {
          toast.error("File too large (max 3MB) — email your PM instead.");
          setSaving(false); return;
        }
        doc_data_url = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        doc_filename = file.name;
      }
      await axios.post(`${API}/public/vendor-upload/${token}`, { section, payload, doc_data_url, doc_filename });
      setSaved(true);
      toast.success("Received — thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save. Try again or reply to the email.");
    }
    setSaving(false);
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="border-white/10 bg-card p-8 max-w-md text-center" data-testid="upload-invalid">
        <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-3" />
        <h1 className="text-lg font-semibold mb-2">Link unavailable</h1>
        <p className="text-sm text-muted-foreground">{err}</p>
        <p className="text-xs text-zinc-500 mt-4">Ask your property manager to send a fresh link.</p>
      </Card>
    </div>
  );

  if (saved) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="border-emerald-500/30 bg-card p-8 max-w-md text-center" data-testid="upload-success">
        <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
        <h1 className="text-lg font-semibold mb-2">Received — thank you</h1>
        <p className="text-sm text-muted-foreground">
          Your property manager has been notified and your compliance status will refresh shortly.
          You can close this tab.
        </p>
      </Card>
    </div>
  );

  if (!ctx) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm text-muted-foreground tracking-widest uppercase">Loading…</div>
    </div>
  );

  const cur = ctx.current || {};

  return (
    <div className="min-h-screen bg-background p-6" data-testid="vendor-upload-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <HardHat className="h-6 w-6 text-orange-400" />
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest">Compliance upload</div>
            <h1 className="text-2xl font-semibold">{ctx.vendor_name}</h1>
          </div>
        </div>

        <Card className="border-white/10 bg-card p-5 mb-4">
          <div className="text-xs text-zinc-500 mb-2">Current status</div>
          <div className="flex flex-wrap gap-2">
            {["coi", "w9", "license"].map((k) => {
              const s = cur[k] || {};
              const label = k === "w9" ? "W-9" : k === "coi" ? "COI" : "License";
              return (
                <Badge key={k} variant="outline" className={s.on_file ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}>
                  {label}: {s.on_file ? (s.expires_at ? `exp ${(s.expires_at || "").slice(0, 10)}` : "on file") : "missing"}
                </Badge>
              );
            })}
          </div>
        </Card>

        <Card className="border-white/10 bg-card p-5">
          <div className="text-xs text-zinc-500 mb-3">Choose what you're updating</div>
          <div className="flex gap-2 mb-4">
            {[["coi", "COI"], ["w9", "W-9"], ["license", "License"]].map(([k, label]) => (
              <button key={k} onClick={() => { setSection(k); setForm({}); }}
                data-testid={`section-${k}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${section === k ? "border-orange-500/40 text-orange-300 bg-orange-500/10" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}>{label}</button>
            ))}
          </div>

          {section === "coi" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Carrier</Label><Input data-testid="up-carrier" value={form.carrier || ""} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" placeholder="State Farm" /></div>
                <div><Label>Policy #</Label><Input value={form.policy_no || ""} onChange={(e) => setForm({ ...form, policy_no: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" placeholder="SF-1234567" /></div>
                <div><Label>Coverage $</Label><Input type="number" value={form.coverage_usd || ""} onChange={(e) => setForm({ ...form, coverage_usd: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" placeholder="1000000" /></div>
                <div><Label>Expires on</Label><Input data-testid="up-expires" type="date" value={form.expires_at || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
              </div>
            </div>
          )}
          {section === "w9" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>EIN last 4</Label><Input data-testid="up-ein" maxLength={4} value={form.ein_last4 || ""} onChange={(e) => setForm({ ...form, ein_last4: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" placeholder="6789" /></div>
                <div><Label>Signed on</Label><Input type="date" value={form.signed_at || ""} onChange={(e) => setForm({ ...form, signed_at: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
              </div>
            </div>
          )}
          {section === "license" && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>License #</Label><Input value={form.number || ""} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
                <div><Label>State</Label><Input value={form.state || ""} maxLength={2} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} className="mt-1 bg-white/[0.04] border-white/10" placeholder="TX" /></div>
                <div><Label>Expires on</Label><Input type="date" value={form.expires_at || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
              </div>
            </div>
          )}

          <div className="mt-4">
            <Label>Attach a copy (optional, PDF or image, max 3 MB)</Label>
            <Input data-testid="up-file" type="file" accept="image/*,application/pdf" onChange={(e) => setForm({ ...form, _file: e.target.files?.[0] })} className="mt-1 bg-white/[0.04] border-white/10" />
          </div>

          <div className="flex items-center gap-3 mt-5">
            <Button data-testid="submit-btn" onClick={submit} disabled={saving} className="bg-orange-500 hover:bg-orange-600 rounded-full">
              <Upload className="h-4 w-4 mr-1" /> {saving ? "Uploading…" : "Submit"}
            </Button>
            <div className="text-xs text-zinc-500 flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Single-use link · expires {(ctx.expires_at || "").slice(0, 10)}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
